import React, { useState } from 'react';
import {
        Box,
        Card,
        Typography,
        Button,
        TextField,
        InputAdornment,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        Chip,
        Grid,
        FormControl,
        InputLabel,
        Select,
        MenuItem,
        useTheme,
} from '@mui/material';
import {
        Search,
        Add,
        ShoppingCart,
        LocalShipping,
        CheckCircle,
        Cancel,
        AccessTime,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, PurchaseOrder as APIPurchaseOrder } from '../../services/simpleApi';

interface PurchaseOrder {
	id: string;
	po_number: string;
	supplier_id: string;
	status: 'draft' | 'ordered' | 'received' | 'cancelled';
	created_at: string;
	total_amount: number;
	items: any[];
}

const ProcurementPage: React.FC = () => {
	const theme = useTheme();
	const [searchQuery, setSearchQuery] = useState('');
	const [orders, setOrders] = useState<PurchaseOrder[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const fetchItems = async () => {
		try {
			setLoading(true);
			const response = await simpleApi.purchaseOrders.list();
			const items: PurchaseOrder[] = response.items.map((po: APIPurchaseOrder) => ({
				id: po.id,
				po_number: po.po_number,
				supplier_id: po.supplier_id,
				status: po.status as any,
				created_at: po.created_at,
				total_amount: po.total_amount,
				items: po.items || [],
			}));
			setOrders(items);
			setError(null);
		} catch (err: any) {
			setError(err.message || 'Failed to load orders');
		} finally {
			setLoading(false);
		}
	};

	React.useEffect(() => {
		fetchItems();
	}, []);

	const clearSuccess = () => setSuccess(null);        const filteredOrders = (orders || []).filter(order =>
                order.id.toLowerCase().includes(searchQuery.toLowerCase())
        );

        const getStatusConfig = (status: string) => {
                switch (status) {
                        case 'received':
                                return { color: 'success', icon: <CheckCircle />, label: 'Received' };
                        case 'ordered':
                                return { color: 'info', icon: <LocalShipping />, label: 'Ordered' };
                        case 'draft':
                                return { color: 'warning', icon: <AccessTime />, label: 'Draft' };
                        case 'cancelled':
                                return { color: 'error', icon: <Cancel />, label: 'Cancelled' };
                        default:
                                return { color: 'default', icon: null, label: status };
                }
        };

        const totalSpend = orders.reduce((sum, order) => sum + order.total_amount, 0);
        const activeOrders = orders.filter(o => o.status === 'ordered').length;

        return (
                <Box>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                                Procurement
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                                Manage purchase orders and supplier procurement
                                        </Typography>
                                </Box>
                                <Button variant="contained" startIcon={<Add />}>
                                        Create Order
                                </Button>
                        </Box>

                        {error && <ErrorAlert error={error} onRetry={fetchItems} />}

                        {/* Stats Cards */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                        <Card>
                                                <Box sx={{ p: 2 }}>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                                                {orders.length}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                                Total Orders
                                                        </Typography>
                                                </Box>
                                        </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                        <Card>
                                                <Box sx={{ p: 2 }}>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                                                                {activeOrders}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                                Active Orders
                                                        </Typography>
                                                </Box>
                                        </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                        <Card>
                                                <Box sx={{ p: 2 }}>
                                                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                                                                £{totalSpend.toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                                Total Spend
                                                        </Typography>
                                                </Box>
                                        </Card>
                                </Grid>
                        </Grid>

                        {/* Search and Filters */}
                        <Card sx={{ mb: 3, p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={6}>
                                                <TextField
                                                        fullWidth
                                                        placeholder="Search orders..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        InputProps={{
                                                                startAdornment: (
                                                                        <InputAdornment position="start">
                                                                                <Search />
                                                                        </InputAdornment>
                                                                ),
                                                        }}
                                                />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                                <FormControl fullWidth>
                                                        <InputLabel>Status</InputLabel>
                                                        <Select label="Status" defaultValue="all">
                                                                <MenuItem value="all">All Status</MenuItem>
                                                                <MenuItem value="draft">Draft</MenuItem>
                                                                <MenuItem value="ordered">Ordered</MenuItem>
                                                                <MenuItem value="received">Received</MenuItem>
                                                        </Select>
                                                </FormControl>
                                        </Grid>
                                </Grid>
                        </Card>

                        {/* Orders Table */}
                        <Card>
                                {loading ? (
                                        <LoadingState message="Loading orders..." />
                                ) : filteredOrders.length === 0 ? (
                                        <EmptyState
                                                title="No purchase orders found"
                                                description="Create your first purchase order to get started"
                                                actionLabel="Create Order"
                                                icon={<ShoppingCart sx={{ fontSize: 64, color: 'text.disabled' }} />}
                                        />
                                ) : (
                                        <TableContainer>
                                                <Table>
                                                        <TableHead>
                                                                <TableRow>
                                                                        <TableCell>Order ID</TableCell>
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>Supplier</TableCell>
                                                                        <TableCell>Items</TableCell>
                                                                        <TableCell>Status</TableCell>
                                                                        <TableCell align="right">Total Amount</TableCell>
                                                                        <TableCell align="right">Actions</TableCell>
                                                                </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                                {filteredOrders.map((order) => {
                                                                        const statusConfig = getStatusConfig(order.status);

                                                                        return (
                                                                                <TableRow key={order.id} hover>
                                                                                        <TableCell>
                                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                                        {order.id.substring(0, 8)}...
                                                                                                </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                                {new Date(order.created_at).toLocaleDateString('en-GB')}
                                                                                        </TableCell>
                                                                                        <TableCell>{order.supplier_id}</TableCell>
                                                                                        <TableCell>{order.items.length} items</TableCell>
                                                                                        <TableCell>
                                                                                                <Chip
                                                                                                        icon={statusConfig.icon || undefined}
                                                                                                        label={statusConfig.label}
                                                                                                        size="small"
                                                                                                        color={statusConfig.color as any}
                                                                                                />
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                                        £{order.total_amount.toLocaleString()}
                                                                                                </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                                <Button size="small">View</Button>
                                                                                        </TableCell>
                                                                                </TableRow>
                                                                        );
                                                                })}
                                                        </TableBody>
                                                </Table>
                                        </TableContainer>
                                )}
                        </Card>

                        <SuccessSnackbar
                                open={!!success}
                                message={success || ''}
                                onClose={clearSuccess}
                        />
                </Box>
        );
};

export default ProcurementPage;
